import { ApiClient, ApiDpopClient, CsrfService } from '@shared/services';
import { API_LATENCY } from '@shared/constants';

import Constants from '@/utils/constants';
import {
	responseErrorHandler,
	apiClientResponseErrorHandler,
	attachBrowserFingerprint,
	attachDeviceFingerprint,
	attachTestMode,
} from '@/data/interceptors';
import {
	sendCustomAnalyticsEvent,
	sendNetworkMetricEvent,
} from '@/utils/AnalyticsUtils';
import TestModeService from '@/utils/testMode.service';
import { REFRESH_TOKEN } from '@/constants';

const csrfService = new CsrfService({
	baseURL: Constants.API_WEB_BASE_URL,
	timeout: Constants.API_TIMEOUT,
	headers: Constants.HEADERS,
	csrfCookieKey: Constants.X_CSRF_COOKIE,
	csrfKey: Constants.X_CSRF_TOKEN,
});

class Api {
	constructor() {
		ApiClient.registerNetworkMetricsObserver({
			sendNetworkMetricEvent: (...data) => sendNetworkMetricEvent(...data),
		});

		/**
		 * @type { ApiClient }
		 */
		this.apiWebClient = new ApiClient({
			withCredentials: true,
			baseURL: Constants.API_WEB_BASE_URL,
			timeout: Constants.API_TIMEOUT,
			headers: Constants.HEADERS,
			refreshToken: this.refreshToken,
		});
		this.apiWebClientWithDeviceFingerprint = new ApiClient({
			withCredentials: true,
			baseURL: Constants.API_WEB_BASE_URL,
			timeout: Constants.API_TIMEOUT,
			headers: Constants.HEADERS,
			refreshToken: this.refreshToken,
		});
		this.apiWebClientDownload = new ApiClient({
			withCredentials: true,
			baseURL: Constants.API_WEB_BASE_URL,
			timeout: 0,
			headers: Constants.HEADERS,
			responseType: 'blob',
			refreshToken: this.refreshToken,
		});
		this.apiClient = new ApiClient({
			withCredentials: true,
			baseURL: Constants.API_BASE_URL,
			timeout: Constants.API_TIMEOUT,
			headers: Constants.HEADERS,
			refreshToken: this.refreshToken,
		});

		this.apiTestModeDPoPClient = new ApiDpopClient({
			withCredentials: false,
			baseURL: Constants.API_TEST_MODE_BASE_URL,
			timeout: Constants.API_TIMEOUT,
			headers: Constants.TEST_CLIENT_HEADERS,
			// refreshToken: this.refreshToken, // NOTE: replace this with actual refresh once supported
		});
		this.apiTestModeDPoPClient.addRequestInterceptors(attachTestMode);

		this.apiWebClient.addRequestInterceptors(attachBrowserFingerprint);
		this.apiWebClient.addRequestInterceptors(csrfService.addCSRFtoReq);

		this.apiClient.addRequestInterceptors(attachBrowserFingerprint);
		this.apiClient.addRequestInterceptors(csrfService.addCSRFtoReq);

		this.apiWebClientDownload.addRequestInterceptors(attachBrowserFingerprint);
		this.apiWebClientDownload.addRequestInterceptors(csrfService.addCSRFtoReq);

		this.apiWebClientWithDeviceFingerprint.addRequestInterceptors(
			attachBrowserFingerprint,
		);
		this.apiWebClientWithDeviceFingerprint.addRequestInterceptors(
			attachDeviceFingerprint,
		);
		this.apiWebClientWithDeviceFingerprint.addRequestInterceptors(
			csrfService.addCSRFtoReq,
		);

		this.apiTestModeDPoPClient.addResponseInterceptors(
			(response) => response,
			responseErrorHandler,
		);
		this.apiWebClient.addResponseInterceptors(
			csrfService.checkCSRFinRes,
			responseErrorHandler,
		);
		this.apiClient.addResponseInterceptors(
			csrfService.checkCSRFinRes,
			apiClientResponseErrorHandler,
		);
		this.apiWebClientDownload.addResponseInterceptors(
			csrfService.checkCSRFinRes,
			responseErrorHandler,
		);
		this.apiWebClientWithDeviceFingerprint.addResponseInterceptors(
			csrfService.checkCSRFinRes,
			responseErrorHandler,
		);
	}

	get dynamicApiWebClient() {
		return TestModeService.isTestModeEnabled()
			? this.apiTestModeDPoPClient
			: this.apiWebClient;
	}

	// Api declaration starts
	login = (data, captcha) =>
		this.apiWebClient.post(`${Constants.SUB_URL_INSIGHTS}/v4/auth/web/login`, {
			data,
			headers: {
				//Conditional captcha inclusion based on two login flows (SMS & EMAIL)
				...(captcha && { [Constants.HCAPTCHA_HEADER_KEY]: captcha }),
				'X-DEVICE-FINGERPRINT': '123',
			},
		});

	otpLoginInitiate = (phoneNumber, captcha, channelType) =>
		this.apiWebClient.post(
			`${Constants.SUB_URL_INSIGHTS}/v2/auth/web/login/initiate`,
			{
				data: {
					type: 'OTP_V2',
					endpoint: phoneNumber,
					channelType,
					deviceFingerprint: '123',
				},
				headers: {
					[Constants.HCAPTCHA_HEADER_KEY]: captcha,
					'X-DEVICE-FINGERPRINT': '123',
				},
			},
		);

	forgotPassword = (params, captcha) =>
		this.apiWebClient.post(
			`${Constants.SUB_URL_INSIGHTS}/v1/auth/forgotPassword`,
			{
				data: {},
				headers: {
					[Constants.HCAPTCHA_HEADER_KEY]: captcha,
				},
				params,
			},
		);

	getRolesList = (params) =>
		this.apiWebClient.get(`${Constants.SUB_URL_INSIGHTS}/v1/user/role`, {
			params: {
				admin: params ? params.admin : false,
			},
		});

	getUserPermissions = () =>
		this.apiWebClient.get(`${Constants.SUB_URL_INSIGHTS}/v1/user/permissions`);

	getUserDetail = () =>
		this.apiWebClient.get(`${Constants.SUB_URL_INSIGHTS}/v1/user/me`);

	logout = () =>
		this.apiWebClient.post(`${Constants.SUB_URL_INSIGHTS}/v1/auth/logout`);

	getUsersList = (params) =>
		this.apiWebClient.get(`${Constants.SUB_URL_INSIGHTS}/v1/user/list`, {
			params,
		});

	deleteUser = (data) =>
		this.apiWebClient.delete(
			`${Constants.SUB_URL_INSIGHTS}/v1/user/${data.userId}`,
		);

	createUser = (data) =>
		this.apiWebClient.post(`${Constants.SUB_URL_INSIGHTS}/v1/user/createUser`, {
			data,
		});

	assignUserRole = (data) =>
		this.apiWebClient.post(
			`${Constants.SUB_URL_INSIGHTS}/v1/user/${data.userId}/role/${data.roleId}`,
		);

	deleteAssignedRole = (data) =>
		this.apiWebClient.put(
			`${Constants.SUB_URL_INSIGHTS}/v1/user/${data.userId}/role/${data.roleId}`,
		);

	getMerchantProfile = () =>
		this.apiWebClient.get(
			`${Constants.SUB_URL_MERCHANT_ONBOARDING}/v1/merchant/profile`,
		);

	getPaymentOptions = () =>
		this.apiWebClient.get(
			`${Constants.SUB_URL_MERCHANT_ONBOARDING}/v1/merchant/paymentOptions`,
		);

	setPassword = (data, captcha) =>
		this.apiWebClient.post(
			`${Constants.SUB_URL_INSIGHTS}/v1/user/setPassword`,
			{
				data,
				headers: {
					[Constants.HCAPTCHA_HEADER_KEY]: captcha,
				},
			},
		);

	// QRCode
	getQRList = (params) =>
		this.apiWebClient.get(`${Constants.SUB_URL_INSIGHTS}/v1/qrpos/list`, {
			params,
		});

	getQRText = (id) =>
		this.apiWebClient.get(
			`${Constants.SUB_URL_MERCHANT_ONBOARDING}/v1/merchant/qrcode/${id}/text`,
		);

	downloadQR = (merchantName, qrCodeId, qrUrl, storeName, terminalName) =>
		this.apiWebClientDownload.post(
			`${Constants.SUB_URL_INSIGHTS}/v1/qrpos/qrdownload`,
			{
				data: {
					merchantName,
					qrCodeId,
					qrUrl,
					storeName,
					terminalName,
				},
			},
		);

	// Notification receiver

	getOTPNotificationReceiver = (qrCodeId, data) =>
		this.apiWebClient.post(
			`${Constants.SUB_URL_MERCHANT_ONBOARDING}/v1/merchant/qrpos/add/QR_CODE/${qrCodeId}/notification`,
			{
				data,
			},
		);

	resendOTPNotificationReceiver = (data) =>
		this.apiWebClient.post(
			`${Constants.SUB_URL_MERCHANT_ONBOARDING}/v1/merchant/qrpos/resendOtp`,
			{
				data,
			},
		);

	verifyOTPNotificationReceiver = (qrCodeId, data) =>
		this.apiWebClient.post(
			`${Constants.SUB_URL_MERCHANT_ONBOARDING}/v1/merchant/qrpos/verify/QR_CODE/${qrCodeId}/otp`,
			{
				data,
			},
		);

	deleteNotificationReceiver = (qrCodeId, data) =>
		this.apiWebClient.put(
			`${Constants.SUB_URL_MERCHANT_ONBOARDING}/v1/merchant/qrpos/remove/QR_CODE/${qrCodeId}/notification`,
			{
				data,
			},
		);

	// Notification receiver

	/**
	 * @deprecated
	 */
	getLiveTransactions = (data) => {
		return this.apiWebClient.post(
			`${Constants.SUB_URL_EARNEST}/v1/transactions/recent`,
			{
				data,
			},
		);
	};

	getLiveTransactionsV2 = (data) => {
		const cancelSource = ApiClient.getCancelController();
		const fetcher = this.apiWebClient.post(
			`${Constants.SUB_URL_EARNEST}/v1/transactions/recent`,
			{
				data,

				signal: cancelSource.signal,
			},
		);
		return { fetcher, cancel: cancelSource.cancel };
	};

	/**
	 * @deprecated
	 */
	getTransactionListNew = (data) =>
		this.apiWebClient.post(
			`${Constants.SUB_URL_INSIGHTS}/v3/transactions/list`,
			{ data },
		);

	getTransactionListV3 = (data) => {
		const cancelSource = ApiClient.getCancelController();
		const response = this.apiWebClient
			.post(`${Constants.SUB_URL_INSIGHTS}/v3/transactions/list`, {
				data,

				signal: cancelSource.signal,
			})
			.then((res) => res.data);
		return { response, cancel: cancelSource.cancel };
	};

	getMerchantPaymentInstruments = (params) =>
		this.apiWebClient.get(
			`${Constants.SUB_URL_INSIGHTS}/v1/merchant/instrumentSolution`,
			{
				params,
			},
		);

	createPaylink = (data) =>
		this.apiWebClient.post(`${Constants.SUB_URL_ATHENA}/v1/paylink`, {
			data,
		});

	resendPaylink = (data) =>
		this.apiWebClient.put(
			`${Constants.SUB_URL_ATHENA}/v1/paylink/${data}/send-reminder`,
		);

	cancelPaylink = (data) =>
		this.apiWebClient.put(
			`${Constants.SUB_URL_ATHENA}/v1/paylink/${data}/cancel`,
		);

	getPaylinks = (data) =>
		this.apiWebClient.post(`${Constants.SUB_URL_INSIGHTS}/v2/payLink/list`, {
			data,
		});

	getTransactionDetail = (data) =>
		this.apiWebClient.post(
			`${Constants.SUB_URL_INSIGHTS}/v3/transactions/details`,
			{
				data,
			},
		);

	getTransactionDetailByUnitId = (unitId) =>
		this.apiWebClient.get(
			`${Constants.SUB_URL_INSIGHTS}/v3/transactions/unit/${unitId}/details`,
		);

	refundTransaction = (data) =>
		this.apiWebClient.post(
			`${Constants.SUB_URL_INSIGHTS}/v1/transactions/backToSource`,
			{
				data,
			},
		);

	reportList = (params) =>
		this.apiWebClient.get(
			`${Constants.SUB_URL_INSIGHTS}/v2/reports/transaction`,
			{
				params,
			},
		);

	getDailySettlements = (data) =>
		this.apiWebClient.post(
			`${Constants.SUB_URL_INSIGHTS}/v1/reports/settlement/month`,
			{ data },
		);

	merchantList = () =>
		this.apiWebClient.get(
			`${Constants.SUB_URL_MERCHANT_ONBOARDING}/v1/merchant/list`, // ${Constants.SUB_URL_INSIGHTS}/v1/merchant/attributes
		);

	downloadReport = (params) =>
		this.apiWebClientDownload.get(
			`${Constants.SUB_URL_INSIGHTS}/v1/reports/${params.id}`,
			{
				params: { selectedMerchant: params.selectedMerchant },
			},
		);

	getMonthlySettlement = (data) =>
		this.apiWebClient.post(
			`${Constants.SUB_URL_INSIGHTS}/v1/reports/settlement/year`,
			{ data },
		);

	getGstInvoice = (data) =>
		this.apiWebClient.post(`${Constants.SUB_URL_INSIGHTS}/v1/reports/gst`, {
			data,
		});

	getRecentSettlements = (data) =>
		this.apiWebClient.post(
			`${Constants.SUB_URL_INSIGHTS}/v1/settlements/changes`,
			{ data },
		);

	downloadRecentSettlementReport = (data) =>
		this.apiWebClientDownload.get(
			`${Constants.SUB_URL_INSIGHTS}/v1/settlements/download/${data}`,
		);

	trends = (data) =>
		this.apiWebClient.post(
			`${Constants.SUB_URL_INSIGHTS}/v3/transactions/metrics/stats`,
			{ data },
		);

	getBatchRefunds = (params) =>
		this.apiWebClient.get(`${Constants.SUB_URL_INSIGHTS}/v1/batch/refunds`, {
			params,
		});
	getSingleBatchFile = (merchantId, requestId) =>
		this.apiWebClient.get(
			`${Constants.SUB_URL_INSIGHTS}/v1/batch/merchantId/${merchantId}/requestId/${requestId}`,
		);
	downloadBatchFile = (batchId) =>
		this.apiWebClientDownload.get(
			`${Constants.SUB_URL_INSIGHTS}/v1/batch/download/${batchId}`,
		);

	downloadBulkRefundTemplate = () =>
		this.apiWebClientDownload.get(
			`${Constants.SUB_URL_INSIGHTS}/v1/batch/template/download`,
		);

	uploadBatchFile = (data) => {
		const formData = new FormData();
		if (data.batchProcessorType)
			formData.append('batchProcessorType', data.batchProcessorType);
		if (data.description) formData.append('description', data.description);
		if (data.comment) formData.append('comment', data.comment);
		if (data.file) formData.append('file', data.file);

		const config = {
			headers: {
				'content-type': 'multipart/form-data',
			},
		};
		return this.apiWebClient.post(
			`${Constants.SUB_URL_INSIGHTS}/v1/batch/process`,
			{
				data,
				headers: config.headers,
			},
		);
	};

	generateReport = (data) =>
		this.apiWebClient.post(
			`${Constants.SUB_URL_INSIGHTS}/v2/reports/transaction/generate`,
			{ data },
		);

	getMerchantHashId = (merchantId) =>
		this.apiWebClient.post(
			`${Constants.SUB_URL_INSIGHTS}/v1/merchant/entityType/MERCHANT/entityId/${merchantId}`,
		);

	getStoreDetail = ({
		merchantId,
		storeId,
		includeImages = true,
		includeAddress = true,
	}) =>
		this.apiWebClient.get(
			`${Constants.SUB_URL_INSIGHTS}/v2/stores/${merchantId}/${storeId}/details?includeImages=${includeImages}&includeAddress=${includeAddress}`,
		);

	updateStore = (merchantId, storeId, data) =>
		this.apiWebClient.patch(
			`${Constants.SUB_URL_MERCHANT_ONBOARDING}/v1/store/${merchantId}/${storeId}`,
			{ data },
		);

	getAddressFromLocation = (latitude, longitude) =>
		this.apiWebClient.get(
			`${Constants.SUB_URL_INSIGHTS}/v1/stores/geolocation/reverseGeocode`,
			{
				params: {
					latitude,
					longitude,
				},
			},
		);

	getAddressFromPinCode = (pincode) =>
		this.apiWebClient.get(
			`${Constants.SUB_URL_INSIGHTS}/v1/stores/geolocation/geocode`,
			{
				params: {
					address: pincode,
				},
			},
		);

	addStoreImage = (data, merchantId, storeId, orderNo, stage, imageType) => {
		const formData = new FormData();
		if (merchantId) formData.append('merchantId', merchantId);
		if (storeId) formData.append('storeId', storeId);
		if (orderNo) formData.append('order_no', orderNo);
		if (stage) formData.append('stage', stage);
		if (imageType) formData.append('type', imageType);
		if (data) formData.append('file', data);

		const config = {
			headers: {
				'content-type': 'multipart/form-data',
			},
		};
		return this.apiWebClient.post(
			`${Constants.SUB_URL_INSIGHTS}/v1/merchant/uploadImage`,
			{
				data: formData,
				headers: config.headers,
			},
		);
	};

	deleteStoreImage = (data) =>
		this.apiWebClient.delete(
			`${Constants.SUB_URL_MERCHANT_ONBOARDING}/v1/merchant/document/${data.imageId}?merchantId=${data.merchantId}&storeId=${data.storeId}`,
		);

	getStorePhoneUpdateOtp = (phoneNumber) =>
		this.apiWebClientWithDeviceFingerprint.post(
			`${Constants.SUB_URL_MERCHANT_ONBOARDING}/v1/store/phoneNumber/requestOtp`,
			{
				data: {
					phoneNumber,
				},
			},
		);

	verifyStorePhoneUpdateOtp = (data) =>
		this.apiWebClientWithDeviceFingerprint.post(
			`${Constants.SUB_URL_MERCHANT_ONBOARDING}/v1/store/phoneNumber/verifyOtp`,
			{
				data: {
					token: data.token,
					otp: data.otp,
					phoneNumber: data.phoneNumber,
					merchantId: data.merchantId,
					storeId: data.storeId,
				},
			},
		);

	getHolidayList = () =>
		this.apiWebClient.get(
			`${Constants.SUB_URL_INSIGHTS}/v1/dashboard/getSettlementHolidayList`,
		);

	getTerminalList = (data) =>
		this.apiWebClient.get(
			`${Constants.SUB_URL_MERCHANT_ONBOARDING}/v1/store/${data.merchantId}/${data.storeId}/terminals`,
			{
				params: {
					skipInactive: data.skipInactive,
					start: data.start,
					limit: data.limit,
				},
			},
		);

	getdailyReportData = (data) =>
		this.apiWebClient.post(
			`${Constants.SUB_URL_INSIGHTS}/v1/reports/transaction`,
			{ data },
		);

	// user profile update

	changeUserData = (userId, data) =>
		this.apiWebClient.put(
			`${Constants.SUB_URL_INSIGHTS}/v1/user/update/${userId}`,
			{ data },
		);

	updateMerchantEmail = (merchantId, data, headers = {}) =>
		this.apiWebClient.put(
			`${Constants.SUB_URL_INSIGHTS}/v1/user/update/merchant/${merchantId}/email`,
			{ data, headers },
		);

	verifyHurdleOTP = (data) =>
		this.apiWebClient.post(
			`${Constants.SUB_URL_INSIGHTS}/v1/user/hurdles/v1/instance/${data.instanceId}/execute`,
			{ data },
		);

	resendOTPForEmailUpdate = (data) =>
		this.apiWebClient.post(
			`${Constants.SUB_URL_INSIGHTS}/v1/user/hurdles/v1/instance/multiotp/request`,
			{ data },
		);

	verifyUser = (verificationKey) =>
		this.apiWebClient.get(
			`${Constants.SUB_URL_INSIGHTS}/v1/auth/verify/${verificationKey}`,
		);

	updateMerchantInfo = (data) =>
		this.apiWebClient.patch(
			`${Constants.SUB_URL_MERCHANT_ONBOARDING}/v1/merchant/profile/update`,
			{ data },
		);

	updateMerchantAddress = (data) =>
		this.apiWebClient.patch(
			`${Constants.SUB_URL_MERCHANT_ONBOARDING}/v1/merchant/profile/update`,
			{ data },
		);

	getGroupSelectionList = () =>
		this.apiWebClient.get(
			`${Constants.SUB_URL_INSIGHTS}/v1/user/groupSelection`,
		); //${Constants.SUB_URL_INSIGHTS}/v1/user/merchant/groupInfoList

	updateUserGroup = (groupId) =>
		this.apiWebClient.post(
			`${Constants.SUB_URL_INSIGHTS}/v1/user/updateSession`,
			{
				data: {
					userGroupId: groupId,
				},
			},
		);

	getUserLinkedStoreAccess = (data) =>
		this.apiWebClient.post(
			`${Constants.SUB_URL_INSIGHTS}/v1/user/listAccessForUser`,
			{ data },
		);

	getStoreList = (params) =>
		this.apiWebClient.get(`${Constants.SUB_URL_INSIGHTS}/v2/stores`, {
			params,
		});

	addStoreAccessToUser = (data) =>
		this.apiWebClient.post(
			`${Constants.SUB_URL_INSIGHTS}/v1/user/addAccessToUser/${data.userId}`,
			{
				data: {
					userGroupId: data.userGroupId,
					entityName: 'STORE',
					entityValue: data.entityValue,
				},
			},
		);

	removeStoreAccessToUser = (data) =>
		this.apiWebClient.put(
			`${Constants.SUB_URL_INSIGHTS}/v1/user/removeAccess/${data.userId}`,
			{
				data: {
					userGroupId: data.userGroupId,
					entityName: 'STORE',
					entityValue: data.entityValue,
				},
			},
		);

	getChatBotToken = () =>
		this.apiWebClient.get(
			`${Constants.SUB_URL_INSIGHTS}/v1/merchant/generateBotToken`,
		);

	refreshToken = () =>
		this.apiWebClient
			.post(`${Constants.SUB_URL_INSIGHTS}/v1/auth/refresh`, {
				data: {},

				isSequential: false,
			})
			.then(() =>
				sendCustomAnalyticsEvent('custom', {
					name: REFRESH_TOKEN,
				}),
			);

	removeAuthCookies = () =>
		this.apiWebClient.get(`${Constants.SUB_URL_INSIGHTS}/v1/user/reset`, {});

	getMerchantOnboardingDetails = () =>
		this.apiWebClient.get(
			`${Constants.SUB_URL_MERCHANT_ONBOARDING}/v1/om/merchant`,
		);

	getChimeraConfig = (data) =>
		this.apiWebClient.post(
			`${Constants.SUB_URL_CHIMERA}/pz/v1/PFB_WEB/merchant/${data.merchantId}/evaluate/bulk`,
			{ data },
		);

	getSettlementReportStatus = (params) =>
		this.apiWebClient.get(
			`${Constants.SUB_URL_INSIGHTS}/v1/reports/${params.merchantId}/${params.urn}/status`,
			{
				params: { settlementDate: params.settlementDate },
			},
		);

	getMonthlySettlementReport = (params) =>
		this.apiWebClient.get(
			`${Constants.SUB_URL_INSIGHTS}/v2/reports/settlement/monthly`,
			{
				params: { ...params },
			},
		);

	generateSettlementReport = (data) =>
		this.apiWebClient.post(
			`${Constants.SUB_URL_INSIGHTS}/v1/reports/settlement/generate`,
			{ data },
		);

	bullhornRequest(config) {
		return this.apiWebClient.post(config.url, {
			data: config.data,
		});
	}

	fetchIrisAccessToken = () =>
		this.apiWebClient.post(`${Constants.SUB_URL_IRISMX}/v1/auth/init`);

	getApiKeysList = (data) =>
		this.apiWebClient.post(
			`${Constants.SUB_URL_OM_INSIGHTS}/v2/merchant/keys/meta-info`,
			{ data },
		);

	getEncryptedApiKeys = (data) =>
		this.apiWebClient.post(
			`${Constants.SUB_URL_OM_INSIGHTS}/v2/merchant/keys/view`,
			{ data },
		);

	updateMerchantConfig = (data) =>
		this.apiWebClient.post(
			`${Constants.SUB_URL_OM_INSIGHTS}/v1/config/update`,
			{ data },
		);

	getDashboardStartupConfigDetails = () =>
		this.apiWebClient.get(
			`${Constants.SUB_URL_OM_INSIGHTS}/v1/dashboard/startup/config`,
		);

	sendFoxtrotEvents = (data) =>
		this.apiClient.post(`${Constants.SUB_URL_UIL}/v5/bulk/ingest`, {
			data,
			skipEvent: [API_LATENCY],
		});

	omReportList = (params) =>
		this.apiWebClient.get(
			`${Constants.SUB_URL_OM_INSIGHTS}/v1/report/transaction`,
			{
				params,
			},
		);

	omGenerateReport = (data) =>
		this.apiWebClient.post(
			`${Constants.SUB_URL_OM_INSIGHTS}/v1/report/transaction/generate`,
			{ data },
		);

	omDownloadReport = (params) =>
		this.apiWebClientDownload.get(
			`${Constants.SUB_URL_OM_INSIGHTS}/v1/report/${params.id}`,
			{
				params: { selectedMerchant: params.selectedMerchant },
			},
		);

	initServiceLineForB2B = (data) =>
		this.apiWebClient.post(
			`${Constants.SUB_URL_OM_INSIGHTS}/v2/merchant/service-line/init`,
			{ data },
		);

	getServiceLineDetails = (serviceLine) =>
		this.apiWebClient.get(
			`${Constants.SUB_URL_OM_INSIGHTS}/v1/service-line/${serviceLine}`,
		);

	getServiceLinesList = (merchantId) =>
		this.apiWebClient.get(
			`${Constants.SUB_URL_MERCHANT_ONBOARDING}/v1/serviceline/merchants/${merchantId}/details`,
		);

	getBusinessSuperCategoryList = () =>
		this.apiWebClient.get(
			`${Constants.SUB_URL_MERCHANT_ONBOARDING}/v1/om/categoryTree/superCategories`,
		);

	// Same API call is used for category and sub category list
	getBusinessCategoryList = (categoryNodeId) =>
		this.apiWebClient.get(
			`${Constants.SUB_URL_MERCHANT_ONBOARDING}/v1/om/categoryTree/node/${categoryNodeId}`,
		);

	getPincodeDetails = (pincode) =>
		this.apiWebClient.get(
			`${Constants.SUB_URL_MERCHANT_ONBOARDING}/v1/om/pincode/${pincode}/details`,
		);

	updateMerchantDetails = (data) =>
		this.apiWebClient.patch(
			`${Constants.SUB_URL_MERCHANT_ONBOARDING}/v1/om/merchant/profile`,
			{ data },
		);

	kycInitV2 = (data) =>
		this.apiWebClient.post(
			`${Constants.SUB_URL_OM_INSIGHTS}/v2/merchant/kyc/init`,
			{ data },
		);
	sentEmailVerification = (data) =>
		this.apiWebClient.post(
			`${Constants.SUB_URL_INSIGHTS}/v1/user/sendVerificationLink`,
			{ data },
		);

	getWebhookEvents = () =>
		this.apiWebClient.get(
			`${Constants.SUB_URL_OM_INSIGHTS}/v1/webhooks/events`,
		);

	getWebhooksList = ({ limit, offset }) =>
		this.apiWebClient.get(
			`${Constants.SUB_URL_OM_INSIGHTS}/v1/webhooks?limit=${limit}&offset=${offset}`,
		);

	createWebhook = (data) =>
		this.apiWebClient.post(`${Constants.SUB_URL_OM_INSIGHTS}/v1/webhooks`, {
			data,
		});

	editWebhook = ({ webhookId, data }) =>
		this.apiWebClient.put(
			`${Constants.SUB_URL_OM_INSIGHTS}/v1/webhooks/${webhookId}`,
			{ data },
		);

	deleteWebhook = (webhookId) =>
		this.apiWebClient.delete(
			`${Constants.SUB_URL_OM_INSIGHTS}/v1/webhooks/${webhookId}`,
		);

	sendOtp = (data, captcha) => {
		return this.apiWebClient.post(
			`${Constants.SUB_URL_INSIGHTS}/v3/auth/web/sendOtp`,
			{
				data,
				headers: {
					[Constants.HCAPTCHA_HEADER_KEY]: captcha,
				},
			},
		);
	};

	submitOtp = (data) =>
		this.apiWebClient.post(`${Constants.SUB_URL_INSIGHTS}/v3/auth/web/login`, {
			data,
		});

	initConsent = (data) =>
		this.apiWebClient.post(`${Constants.SUB_URL_OM_INSIGHTS}/v1/consent/init`, {
			data,
		});

	fetchPoliciesPreview = (data) =>
		this.apiWebClient.post(
			`${Constants.SUB_URL_OM_INSIGHTS}/v1/consent/approve`,
			{
				data,
			},
		);

	downloadPolicies = (data) =>
		this.apiWebClientDownload.post(
			`${Constants.SUB_URL_OM_INSIGHTS}/v1/document/download`,
			{
				data,
			},
		);

	initiateMira = (merchantId, data) =>
		this.apiWebClient.post(
			`${Constants.SUB_URL_MERCHANT_ONBOARDING}/v1/crawler/${merchantId}/init`,
			{
				data,
			},
		);

	getMiraReport = (merchantId) =>
		this.apiWebClient.get(
			`${Constants.SUB_URL_MERCHANT_ONBOARDING}/v1/crawler/${merchantId}/report`,
		);

	geMerchantJourneyStatus = (data) =>
		this.apiWebClient.post(
			`${Constants.SUB_URL_MERCHANT_ONBOARDING}/v1/om/journey`,
			{ data },
		);

	/**
	 * @param {referralCode} - string
	 * @returns {Promise} - A promise that resolves with the masked details
	 */
	fetchMaskedDetails = (referralCode) =>
		this.apiWebClient.get(
			`${Constants.SUB_URL_OM_INSIGHTS}/v1/referrals/fetchMaskedDetails/${referralCode}`,
		);

	/**
	 * @param {ValidationData} data - {phoneNumber: string, referralCode: string}
	 * @returns {Promise} - A promise that resolves with the validation result
	 */
	validateMaskedDetails = (data) =>
		this.apiWebClient.post(
			`${Constants.SUB_URL_OM_INSIGHTS}/v1/referrals/validate`,
			{
				data,
			},
		);

	verifyMerchantLoginRedirection = (data) =>
		this.apiWebClient.post(
			`${Constants.SUB_URL_OM_INSIGHTS}/v1/merchant/auth/callback`,
			{ data },
		);

	getMerchantAccountDetails = ({ merchantId }) =>
		this.apiWebClient.get(
			`${Constants.SUB_URL_VALHALLA}/v1/pfb/getAccountDetails/${merchantId}`,
		);

	getBankDetailsUsingIfsc = ({ ifsc }) =>
		this.apiWebClient.get(
			`${Constants.SUB_URL_MERCHANT_ONBOARDING}/v1/branch/${ifsc}`,
		);

	getSimulatorAuth = () =>
		this.apiWebClient.get(
			`${Constants.SUB_URL_OM_INSIGHTS}/v1/merchant/auth/simulator`,
		);

	simulatorLogin = (data) =>
		this.apiTestModeDPoPClient.post(
			`${Constants.SUB_URL_SIMULATOR}/v1/auth/primer/login`,
			data,
		);

	getApiTestKeys = () =>
		this.dynamicApiWebClient.post(
			`${Constants.SUB_URL_SIMULATOR}/v1/auth/generate-test-keys`,
		);
}

export default Api;
